import { memo } from 'react';
import { Box } from '@mui/material';

type HighlightTextProps = {
  text: string;
  highlight: string;
};

/**
 * Renders text with matching portions highlighted.
 * Case-insensitive matching. Returns plain text if no highlight term.
 * Highlight colors adapt to the current MUI palette mode so the marker stays
 * readable in both light and dark themes (light text on yellow is unreadable,
 * so dark mode uses a saturated amber with forced dark text).
 */
const HighlightTextComponent = ({ text, highlight }: HighlightTextProps) => {
  if (!highlight.trim() || !text) {
    return <>{text}</>;
  }

  const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);

  if (parts.length === 1) {
    return <>{text}</>;
  }

  const lowerHighlight = highlight.trim().toLowerCase();

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === lowerHighlight ? (
          <Box
            key={`part-${i}`}
            component="mark"
            sx={(theme) => ({
              backgroundColor: theme.palette.mode === 'dark' ? '#fbc02d' : '#fff176',
              color: theme.palette.mode === 'dark' ? '#000' : 'inherit',
              padding: 0,
              borderRadius: '2px',
            })}
          >
            {part}
          </Box>
        ) : (
          part
        )
      )}
    </>
  );
};

export const HighlightText = memo(HighlightTextComponent);
